/**
 * Novu Client Wrapper
 *
 * This is a low-level wrapper around @novu/js Client API.
 * Implements caching, WebSocket listeners, and state management.
 *
 * This mimics the internal implementation of Novu's Web Component.
 */

import { Novu } from '@novu/js'
import type {
  Notification,
  ListNotificationsResponse,
  NotificationFilter,
} from '@novu/js'
import axios from 'axios'
import {
  applyInlinePayload,
  applyMessage,
  hasInlinePayload,
  isBatchUnsupportedError,
  type InboxMessage,
} from './notification-enrichment'

/** 后端批量接口单次上限（api NOTIFICATION_MESSAGES_BATCH_MAX） */
const MESSAGES_BATCH_MAX = 50
/**
 * 批量接口不可用时（prod 后端未上）逐条补齐的单次上限：缓存里可能有上百条历史通知，
 * 逐条全补会一口气打上百个 GET；只补靠前的（就是首屏看得见的），其余等再次打开 / 点击时补。
 */
const MESSAGES_SINGLE_FALLBACK_MAX = 20

export interface NotificationCacheEntry {
  notifications: Notification[]
  hasMore: boolean
  filter: NotificationFilter
  timestamp: number
}

export interface NotificationListOptions extends NotificationFilter {
  limit?: number
  after?: string
}

export type NotificationEventType =
  | 'notification_received'
  | 'unread_count_changed'
  | 'cache_updated'
  | 'new_notification_available' // New event for UI notification banner
  | 'sync_needed' // New event for sync banner when inbox is open
  | 'notifications_enriched' // 后台补齐 payload 完成；UI 收到后对当前列表调 applyKnownEnrichment 原地替换

export type NotificationEventHandler = (data?: any) => void

/**
 * Novu Client Wrapper with Built-in Caching
 */
export class NovuClientWrapper {
  private client: Novu
  private cache: Map<string, NotificationCacheEntry> = new Map()
  private listeners: Map<NotificationEventType, Set<NotificationEventHandler>> = new Map()
  private cacheDuration = 5 * 60 * 1000 // 5 minutes
  private wsInitialized = false
  private inboxOpenState = false // Track if inbox is currently open
  private currentTab: 'inbox' | 'archived' = 'inbox' // Track current active tab
  private pendingNotifications: Notification[] = [] // Store notifications received while inbox is open
  private subscriberId: string = '' // Store subscriberId for API calls
  private needsSyncInbox = false // Track if Inbox needs sync when opened
  private needsSyncArchive = false // Track if Archive needs sync when opened
  private isHandlingNewNotification = false // Track if currently handling new notification
  private syncDebounceTimer: NodeJS.Timeout | null = null // Debounce timer for sync events
  private syncDebounceDelay = 1000 // 1 second debounce delay
  private isLocalOperation = false // Track if currently performing a local operation
  private localOperationTimer: NodeJS.Timeout | null = null // Timer to clear local operation flag

  // ---- payload 补齐（enrich）状态 ----
  /** transactionId → 后端 message（null = 查过、后端没有；不再重查） */
  private resolvedMessages: Map<string, InboxMessage | null> = new Map()
  /** transactionId → 进行中的查询，同一 id 并发只发一次 */
  private inflightMessages: Map<string, Promise<void>> = new Map()
  /** 批量接口是否可用；404 / 405 后本会话（本 wrapper 实例，登出即重建）不再尝试 */
  private batchSupported = true
  /** 会话代数：clearAllCache（登出 / 切账号）+1，旧代发出去的请求回来后丢弃 */
  private epoch = 0
  /** 无分页的 list() 进行中请求，同一过滤条件并发只打一次 Novu */
  private inflightLists: Map<string, Promise<{ data?: ListNotificationsResponse; error?: Error }>> = new Map()

  constructor(client: Novu) {
    this.client = client
    // Extract subscriberId from client
    this.subscriberId = (client as any)._subscriberId || (client as any).subscriberId || ''
    console.log('[NovuClientWrapper] Initialized with subscriberId:', this.subscriberId)
    this.initializeWebSocket()
  }

  /**
   * Set inbox open/close state
   */
  setInboxOpen(isOpen: boolean): void {
    this.inboxOpenState = isOpen

    // DON'T clear pending notifications when inbox closes
    // User may not have clicked the "Load new notifications" button yet
    // Pending notifications should only be cleared when:
    // 1. User clicks the load button
    // 2. User switches tabs (which triggers a refetch)
  }

  /**
   * Mark that a local operation is starting
   * This prevents sync banners from showing for operations initiated by this client
   */
  private startLocalOperation(): void {
    this.isLocalOperation = true
    console.log('[NovuClientWrapper] 🔧 Local operation started')

    // Clear any existing timer
    if (this.localOperationTimer) {
      clearTimeout(this.localOperationTimer)
    }

    // Auto-clear the flag after 3 seconds (should be enough for WebSocket to fire)
    this.localOperationTimer = setTimeout(() => {
      this.isLocalOperation = false
      this.localOperationTimer = null
      console.log('[NovuClientWrapper] ✅ Local operation flag cleared (timeout)')
    }, 3000)
  }

  /**
   * Set current active tab
   */
  setCurrentTab(tab: 'inbox' | 'archived'): void {
    this.currentTab = tab
    console.log('[NovuClientWrapper] Current tab changed to:', tab)
  }

  /**
   * Get current active tab
   */
  getCurrentTab(): 'inbox' | 'archived' {
    return this.currentTab
  }

  /**
   * Get pending notifications count
   */
  getPendingCount(): number {
    return this.pendingNotifications.length
  }

  /**
   * Check if Inbox needs sync
   */
  needsInboxSync(): boolean {
    return this.needsSyncInbox
  }

  /**
   * Check if Archive needs sync
   */
  needsArchiveSync(): boolean {
    return this.needsSyncArchive
  }

  /**
   * Clear sync flags
   */
  clearSyncFlags(): void {
    this.needsSyncInbox = false
    this.needsSyncArchive = false
  }

  /**
   * Compare notifications to detect changes
   * Returns comparison result with details
   */
  /**
   * Validate and sync cache when inbox is closed
   * Simply delegates to syncCacheFromFeedAPI for consistency
   */
  private async validateAndSyncCache(): Promise<void> {
    console.log('[NovuClientWrapper] 🔄 Validating cache (Inbox is closed)')

    // Record inbox state at the start of API call
    const wasInboxClosed = !this.inboxOpenState

    try {
      await this.syncCacheFromFeedAPI()

      // After sync, check if inbox state changed during the API call
      const isInboxNowOpen = this.inboxOpenState

      if (wasInboxClosed && isInboxNowOpen) {
        // Inbox was opened during the API call
        // Mark for sync to show banner instead of auto-updating UI
        console.log('[NovuClientWrapper] 📬 Inbox opened during sync, marking for sync banner')
        this.needsSyncInbox = true
        this.needsSyncArchive = true
        this.emit('sync_needed', { tab: 'both', reason: 'inbox_opened_during_sync' })
      }
    } catch (error) {
      console.error('[NovuClientWrapper] Failed to validate cache:', error)
      this.needsSyncInbox = true
      this.needsSyncArchive = true
    }
  }

  /**
   * Initialize WebSocket listeners
   */
  private initializeWebSocket() {
    if (this.wsInitialized) return

    // Listen to notification received event
    this.client.on('notifications.notification_received', async (event) => {
      console.log('[NovuClientWrapper] Notification received via WS')

      // Mark that we're handling a new notification
      this.isHandlingNewNotification = true

      let notification = event?.result as Notification | undefined

      // 补齐 payload：新通知 data 自带 payload 镜像（inboxDataVersion "1"）直接合并、不发请求；
      // 旧格式（workflow 未升级的环境）才查后端。WS 这条不在首屏渲染路径上（收件箱关着进缓存、
      // 开着进 pending banner），所以这里等补完再入队，跟 list() 的结果保持一致。
      if (notification) {
        notification = await this.ensureEnriched(notification)
      }

      if (this.inboxOpenState) {
        // Inbox is OPEN: Add to pending notifications only
        // DON'T update cache - let user click the banner to load new notifications
        console.log('[NovuClientWrapper] Inbox is open, adding to pending notifications')
        if (notification) {
          // 按 id 去重 — WS 事件如果重发 (断连重连等场景) 同一条通知会进队两次,
          // 用户点 banner 时 mergedList 长度变多甚至 React key 冲突。
          // 上方 if (notification) 已收窄,但 let 变量在闭包内不保留收窄,用 ! 断言
          const exists = this.pendingNotifications.some(n => n.id === notification!.id)
          if (!exists) {
            this.pendingNotifications.push(notification)
          } else {
            console.log('[NovuClientWrapper] Skipping duplicate pending notification:', notification.id)
          }
        }

        // Always emit the event - UI will decide whether to show banner
        this.emit('new_notification_available', {
          notification,
          count: this.pendingNotifications.length,
        })
      } else {
        // Inbox is CLOSED: Update cache directly
        console.log('[NovuClientWrapper] Inbox is closed, updating cache directly')
        this.updateCacheWithNewNotification(notification)
      }

      // Always emit the general event
      this.emit('notification_received', event)

      // Clear the flag after a delay to allow API to sync
      setTimeout(() => {
        this.isHandlingNewNotification = false
        console.log('[NovuClientWrapper] Finished handling new notification')
      }, 2000) // 2 seconds should be enough for API to sync
    })

    // Listen to unread count changed event
    this.client.on('notifications.unread_count_changed', async (data) => {
      console.log('[NovuClientWrapper] Unread count changed via WS:', data)
      console.log('[NovuClientWrapper] State check:', {
        isLocalOperation: this.isLocalOperation,
        isHandlingNewNotification: this.isHandlingNewNotification,
        inboxOpenState: this.inboxOpenState
      })

      // If this is a local operation, skip sync banner
      if (this.isLocalOperation) {
        console.log('[NovuClientWrapper] ⏭️ Skipping sync banner (local operation)')
        this.emit('unread_count_changed', data)
        return
      }

      // If we're currently handling a new notification, skip validation
      // because the new notification was already added to cache via updateCacheWithNewNotification
      if (this.isHandlingNewNotification) {
        console.log('[NovuClientWrapper] ⏭️ Skipping validation (currently handling new notification)')
        this.emit('unread_count_changed', data)
        return
      }

      // Check if inbox is closed
      if (!this.inboxOpenState) {
        // Inbox is CLOSED: Validate cache after a short delay
        // We use direct API call now, so this will get fresh data
        const delayMs = 1000
        console.log(`[NovuClientWrapper] 📭 Inbox closed, will validate cache after ${delayMs}ms`)

        setTimeout(async () => {
          console.log('[NovuClientWrapper] 📭 Validating cache with direct API call')
          await this.validateAndSyncCache()
        }, delayMs)
      } else {
        console.log('[NovuClientWrapper] 📬 Inbox is open, debouncing sync event')

        // Clear existing debounce timer
        if (this.syncDebounceTimer) {
          console.log('[NovuClientWrapper] ⏱️ Clearing previous sync debounce timer')
          clearTimeout(this.syncDebounceTimer)
        }

        // Inbox is OPEN: Debounce sync events to avoid banner flashing
        // This handles cases where multiple operations happen in quick succession on other devices
        this.syncDebounceTimer = setTimeout(() => {
          console.log('[NovuClientWrapper] 🔔 Debounce timer fired, marking tabs for sync')

          // Mark current tab for sync
          if (this.currentTab === 'inbox') {
            console.log('[NovuClientWrapper] 🔔 Marking Inbox tab for sync (remote change detected)')
            this.needsSyncInbox = true
            this.emit('sync_needed', { tab: 'inbox', reason: 'remote_change' })
          } else if (this.currentTab === 'archived') {
            console.log('[NovuClientWrapper] 🔔 Marking Archive tab for sync (remote change detected)')
            this.needsSyncArchive = true
            this.emit('sync_needed', { tab: 'archive', reason: 'remote_change' })
          }

          // Also mark the opposite tab for sync (because operations may affect both tabs)
          // For example: archiving a message affects both Inbox and Archive
          if (this.currentTab === 'inbox') {
            this.needsSyncArchive = true
            console.log('[NovuClientWrapper] 🔔 Also marking Archive tab for sync')
          } else {
            this.needsSyncInbox = true
            console.log('[NovuClientWrapper] 🔔 Also marking Inbox tab for sync')
          }

          // Clear the timer reference
          this.syncDebounceTimer = null
        }, this.syncDebounceDelay)

        console.log(`[NovuClientWrapper] ⏱️ Sync event debounced for ${this.syncDebounceDelay}ms`)
      }

      // Still emit the event for other listeners
      this.emit('unread_count_changed', data)
    })

    this.wsInitialized = true
  }

  /**
   * Update cache with new notification (when inbox is closed)
   */
  private updateCacheWithNewNotification(notification?: Notification): void {
    if (!notification) return

    // Update all relevant caches
    this.cache.forEach((entry, key) => {
      // Add notification to the beginning of the list if it matches the filter
      const filter = entry.filter

      // Check if notification matches this cache's filter
      const matchesFilter = this.notificationMatchesFilter(notification, filter)

      if (matchesFilter) {
        // 按 id 去重 — WS 事件可能因为重连 / 重发 / 时序对同一条通知触发多次,
        // 没去重就会在 cache 里塞两份 → 渲染层报 "two children with the same key"。
        // 已存在的话:覆盖旧那条(用新数据 + 移到顶部),保证只有一份且 fresh。
        const filtered = entry.notifications.filter(n => n.id !== notification.id)
        const updatedNotifications = [notification, ...filtered]
        this.cache.set(key, {
          ...entry,
          notifications: updatedNotifications,
          timestamp: Date.now(), // Refresh cache timestamp
        })
        console.log('[NovuClientWrapper] Updated cache:', key, 'with new notification')
      }
    })
  }

  /**
   * Check if notification matches filter
   */
  private notificationMatchesFilter(
    notification: Notification,
    filter: NotificationFilter
  ): boolean {
    // Check read status
    if (filter.read !== undefined && notification.isRead !== filter.read) {
      return false
    }

    // Check archived status
    if (filter.archived !== undefined && notification.isArchived !== filter.archived) {
      return false
    }

    // Check tags
    if (filter.tags && filter.tags.length > 0) {
      const notificationTags = notification.tags || []
      const hasMatchingTag = filter.tags.some((tag) => notificationTags.includes(tag))
      if (!hasMatchingTag) {
        return false
      }
    }

    return true
  }

  /**
   * Generate cache key from filter
   */
  private getCacheKey(filter: NotificationFilter): string {
    // Build cache key with only defined fields to ensure consistency
    const key: Record<string, any> = {
      tags: filter.tags || [],
    }

    // Only add fields that are explicitly set
    if (filter.read !== undefined) key.read = filter.read
    if (filter.archived !== undefined) key.archived = filter.archived
    if (filter.snoozed !== undefined) key.snoozed = filter.snoozed

    return JSON.stringify(key)
  }

  /**
   * Check if cache is valid
   */
  private isCacheValid(cacheKey: string): boolean {
    const entry = this.cache.get(cacheKey)
    if (!entry) return false

    const now = Date.now()
    return now - entry.timestamp < this.cacheDuration
  }

  // ==================== payload 补齐（enrich） ====================
  //
  // Novu inbox list / WS push 的 notification.data 历史上不含完整 trigger payload，
  // msg-mentioned / msg-send 等要 payload 才能渲染发送人、关联实体。补齐顺序：
  //   ① data.inboxDataVersion === "1"（2026-11-02 起的新通知）：data 自带 payload 镜像，直接合并，不发请求；
  //   ② 其余（历史通知）攒成一次 POST /pms/notifications/messages/batch；
  //   ③ 批量接口 404 / 405（后端还没上）→ 退回逐条 GET /pms/notifications/messages，并记住本会话不支持批量。
  // 补齐在后台进行，不阻塞 list() 返回（首屏先按 Novu 原数据渲染），补完更新缓存并发
  // `notifications_enriched`，UI 按 id 替换。合并口径见 ./notification-enrichment（合并、不替换 data）。

  /** 这条还需要查后端补 payload 吗（自带 payload 镜像 / 已查过 → 不需要）。 */
  needsEnrichment(notification: Notification | null | undefined): boolean {
    const tx = notification?.transactionId
    if (!tx || !this.subscriberId) return false
    if (hasInlinePayload(notification)) return false
    return !this.resolvedMessages.has(tx)
  }

  /**
   * 用已知数据补齐（inline payload / 已查到的 message），不发请求；返回新对象，
   * 没有可补的返回同一引用（避免无谓重渲染）。
   */
  applyKnownEnrichment<T extends Notification>(notification: T): T {
    if (!notification) return notification
    if (hasInlinePayload(notification)) return applyInlinePayload(notification)
    const tx = notification.transactionId
    if (tx && this.resolvedMessages.has(tx)) return applyMessage(notification, this.resolvedMessages.get(tx))
    return notification
  }

  /**
   * 确保这一条补齐（点击跳转、WS 推送用）：还没补就当场查一次（与进行中的请求合并），返回补齐后的对象。
   * 查不到 / 失败 → 原样返回（降级渲染），不抛错。
   */
  async ensureEnriched<T extends Notification>(notification: T): Promise<T> {
    if (!notification) return notification
    const epoch = this.epoch
    if (this.needsEnrichment(notification)) {
      const tx = notification.transactionId
      // 搭了别人的车（进行中的批次）但那一批没覆盖到它时，自己再单独查一次
      const joined = this.inflightMessages.has(tx)
      await this.fetchMessages([tx])
      if (joined && epoch === this.epoch && this.needsEnrichment(notification)) {
        await this.fetchMessages([tx])
      }
    }
    if (epoch !== this.epoch) return notification
    return this.applyKnownEnrichment(notification)
  }

  /** 后台补齐一批（不 await）；补完刷新缓存并通知 UI。 */
  private enrichInBackground(notifications: Notification[]): void {
    const cap = this.batchSupported ? MESSAGES_BATCH_MAX : MESSAGES_SINGLE_FALLBACK_MAX
    const txIds = Array.from(
      new Set(notifications.filter((n) => this.needsEnrichment(n)).map((n) => n.transactionId))
    ).slice(0, cap)
    if (txIds.length === 0) return

    const epoch = this.epoch
    void this.fetchMessages(txIds)
      .then(() => {
        // 登出 / 切账号后回来的结果直接丢弃
        if (epoch !== this.epoch) return
        const resolved = txIds.filter((tx) => this.resolvedMessages.has(tx))
        if (resolved.length === 0) return
        this.refreshCachedEnrichment()
        this.emit('notifications_enriched', { transactionIds: resolved })
      })
      .catch((error) => {
        console.error('[NovuClientWrapper] background enrichment failed:', error)
      })
  }

  /** 把已知补齐结果应用到所有缓存条目和 pending 队列。 */
  private refreshCachedEnrichment(): void {
    this.cache.forEach((entry, key) => {
      let changed = false
      const notifications = entry.notifications.map((n) => {
        const next = this.applyKnownEnrichment(n)
        if (next !== n) changed = true
        return next
      })
      if (changed) this.cache.set(key, { ...entry, notifications })
    })
    this.pendingNotifications = this.pendingNotifications.map((n) => this.applyKnownEnrichment(n))
  }

  /**
   * 查一批 transactionId 的 message，结果进 resolvedMessages。已查过的跳过、进行中的合并等待，
   * 同一 id 并发只发一次请求。永不 reject。
   */
  private async fetchMessages(txIds: string[]): Promise<void> {
    const epoch = this.epoch
    const waits: Promise<void>[] = []
    const todo: string[] = []
    for (const tx of new Set(txIds)) {
      if (this.resolvedMessages.has(tx)) continue
      const pending = this.inflightMessages.get(tx)
      if (pending) waits.push(pending)
      else todo.push(tx)
    }
    if (todo.length > 0) {
      const request: Promise<void> = this.requestMessages(todo, epoch).finally(() => {
        for (const tx of todo) {
          if (this.inflightMessages.get(tx) === request) this.inflightMessages.delete(tx)
        }
      })
      for (const tx of todo) this.inflightMessages.set(tx, request)
      waits.push(request)
    }
    await Promise.all(waits)
  }

  /**
   * 实际发请求：批量优先，404 / 405 降级逐条。走后端代理：业务 JWT 鉴权（全局 axios 拦截器附加），
   * subscriberId 由后端强制 = ctx.user.id。全局响应拦截器已把 { data } 解包。
   * 失败（超时 / 上游 5xx / 无权限）silent skip，不记 resolved，下次再试。
   */
  private async requestMessages(txIds: string[], epoch: number): Promise<void> {
    const store = (tx: string, message: InboxMessage | null | undefined) => {
      if (epoch === this.epoch) this.resolvedMessages.set(tx, message ?? null)
    }

    if (this.batchSupported) {
      try {
        for (let i = 0; i < txIds.length; i += MESSAGES_BATCH_MAX) {
          const chunk = txIds.slice(i, i + MESSAGES_BATCH_MAX)
          const byTx = (await axios.post(
            '/pms/notifications/messages/batch',
            { transactionIds: chunk, channel: 'in_app' },
            // 后端没上这个接口时是 404：属于预期的探测结果，不弹全局 toast
            { suppressNotFoundToast: true } as Record<string, unknown>
          )) as unknown as Record<string, InboxMessage | null> | null
          for (const tx of chunk) store(tx, byTx?.[tx])
        }
        return
      } catch (error) {
        if (!isBatchUnsupportedError(error)) {
          console.error('[NovuClientWrapper] messages/batch failed:', error)
          return
        }
        console.info('[NovuClientWrapper] messages/batch not available, falling back to per-message requests')
        this.batchSupported = false
      }
    }

    const singles = txIds
      .filter((tx) => !this.resolvedMessages.has(tx))
      .slice(0, MESSAGES_SINGLE_FALLBACK_MAX)
    await Promise.all(
      singles.map(async (tx) => {
        try {
          const message = (await axios.get('/pms/notifications/messages', {
            params: { transactionId: tx, channel: 'in_app' },
          })) as unknown as InboxMessage | null
          store(tx, message)
        } catch (error) {
          console.error('[NovuClientWrapper] messages fetch failed:', tx, error)
        }
      })
    )
  }

  /**
   * List notifications with caching
   */
  async list(options: NotificationListOptions = {}): Promise<{
    data?: ListNotificationsResponse
    error?: Error
  }> {
    const { limit = 20, after, ...filter } = options
    const cacheKey = this.getCacheKey(filter)

    // Return from cache if valid and no pagination
    if (!after && this.isCacheValid(cacheKey)) {
      const entry = this.cache.get(cacheKey)!

      console.log('[NovuClientWrapper] Returning from cache:', cacheKey, 'with', entry.notifications.length, 'notifications')

      // 缓存里可能有 UI 回写（updateCacheManually）的未补齐引用，先套一遍已知结果；
      // 还没查过的（例如 syncCacheFromFeedAPI 刚换进来的新条目）后台补
      const notifications = entry.notifications.map((n) => this.applyKnownEnrichment(n))
      if (notifications.some((n, i) => n !== entry.notifications[i])) {
        this.cache.set(cacheKey, { ...entry, notifications })
      }
      this.enrichInBackground(notifications)

      // Return ALL cached notifications (including empty cache)
      // Empty cache is valid state - it means this tab truly has no notifications
      return {
        // SDK 的 ListNotificationsResponse 还声明了 filter 字段,缓存返回路径历史上
        // 不带它(消费方未读取),cast 保持原始返回形状不变
        data: {
          notifications,
          hasMore: entry.hasMore,
        } as ListNotificationsResponse,
      }
    }

    // 同一过滤条件的首屏请求并发时只打一次 Novu（开发态 StrictMode 双跑 effect、
    // 打开收件箱与同步几乎同时触发等场景），后来者等同一个结果
    if (!after) {
      const inflightKey = `${cacheKey}|${limit}`
      const pending = this.inflightLists.get(inflightKey)
      if (pending) return pending
      const request = this.fetchList(filter, cacheKey, limit, after).finally(() => {
        if (this.inflightLists.get(inflightKey) === request) this.inflightLists.delete(inflightKey)
      })
      this.inflightLists.set(inflightKey, request)
      return request
    }
    return this.fetchList(filter, cacheKey, limit, after)
  }

  private async fetchList(
    filter: NotificationFilter,
    cacheKey: string,
    limit: number,
    after: string | undefined
  ): Promise<{
    data?: ListNotificationsResponse
    error?: Error
  }> {
    // Fetch from API
    console.log('[NovuClientWrapper] Fetching from API:', cacheKey, { after })

    try {
      // 统一用 SDK 的 Inbox list API（与 mobile 一致）。
      // 之前首屏走的 legacy Feed API (/v1/subscribers/.../notifications/feed) 不认 archived
      // query param，导致 Archived tab 永远拿不到归档数据；详见 issue 调试记录。
      console.log('[NovuClientWrapper] Fetching via SDK list API', { archived: filter.archived, limit, after })
      const epoch = this.epoch
      const response: any = await this.client.notifications.list({
        ...filter,
        limit,
        after,
      })

      // 请求期间登出 / 切账号：上一个会话的结果不进缓存
      if (epoch !== this.epoch) {
        return { error: new Error('[NovuClientWrapper] session changed during list()') }
      }

      if (response.data) {
        // 补齐 payload：已知的（自带 payload 镜像 / 本会话查过的）同步合并；其余在后台补，
        // **不 await** —— 首屏先按 Novu 原数据渲染，补完更新缓存并发 notifications_enriched。
        response.data.notifications = response.data.notifications.map((n: Notification) =>
          this.applyKnownEnrichment(n)
        )
        this.enrichInBackground(response.data.notifications)

        // 防御性按 id 去重 — SDK 理论上不返回重复,但 race / 重试 / inbox API 抖动
        // 都可能让两份同 id notification 进 cache → render 层报 React duplicate key。
        const dedupedNotifications = Array.from(
          new Map(response.data.notifications.map((n: Notification) => [n.id, n])).values()
        ) as Notification[]
        if (dedupedNotifications.length !== response.data.notifications.length) {
          console.warn(
            '[NovuClientWrapper] Dropped',
            response.data.notifications.length - dedupedNotifications.length,
            'duplicate notification(s) from list() response'
          )
          response.data.notifications = dedupedNotifications
        }

        if (!after) {
          // Initial fetch - cache the data
          this.cache.set(cacheKey, {
            notifications: response.data.notifications,
            hasMore: response.data.hasMore,
            filter,
            timestamp: Date.now(),
          })
          this.emit('cache_updated', { filter, data: response.data })
        } else {
          // Pagination fetch (load more)
          if (response.data.notifications.length === 0) {
            // No more data - just update hasMore flag, keep existing notifications
            console.log('[NovuClientWrapper] Load more returned empty, no more data available')
            const entry = this.cache.get(cacheKey)
            if (entry) {
              this.cache.set(cacheKey, {
                ...entry,
                hasMore: false, // Update hasMore to false
                timestamp: Date.now(),
              })
            }
            // Return response without emitting cache_updated
            // This prevents UI from replacing current data with empty array
            return response
          }

          // Has new data - append to cache
          const entry = this.cache.get(cacheKey)
          if (entry) {
            const notificationMap = new Map<string, Notification>()

            // Add existing notifications first (these have correct state)
            entry.notifications.forEach(n => notificationMap.set(n.id, n))

            // Add new notifications ONLY if they don't exist yet
            // This prevents SDK data from overwriting local state
            response.data.notifications.forEach((n: Notification) => {
              if (!notificationMap.has(n.id)) {
                // New notification, add it
                notificationMap.set(n.id, n)
              }
              // If notification already exists in cache, skip it
              // This preserves the correct state (read/unread) from cache
            })

            const mergedNotifications = Array.from(notificationMap.values())

            // Update cache with merged data
            this.cache.set(cacheKey, {
              notifications: mergedNotifications,
              hasMore: response.data.hasMore,
              filter,
              timestamp: Date.now(),
            })

            console.log('[NovuClientWrapper] Updated cache with pagination data:',
              'previous:', entry.notifications.length,
              'new:', response.data.notifications.length,
              'total:', mergedNotifications.length,
              'newAdded:', mergedNotifications.length - entry.notifications.length
            )

            this.emit('cache_updated', { filter, data: { notifications: mergedNotifications, hasMore: response.data.hasMore } })
          }
        }
      }

      return response
    } catch (error) {
      return { error: error as Error }
    }
  }

  /**
   * Mark notification as read
   */
  async read(notificationId: string): Promise<void> {
    this.startLocalOperation()
    await this.client.notifications.read({ notificationId })
    this.updateNotificationInCache(notificationId, { isRead: true })
  }

  /**
   * Mark notification as unread
   */
  async unread(notificationId: string): Promise<void> {
    this.startLocalOperation()
    await this.client.notifications.unread({ notificationId })
    this.updateNotificationInCache(notificationId, { isRead: false })
  }

  /**
   * Archive notification
   */
  async archive(notificationId: string): Promise<void> {
    this.startLocalOperation()
    await this.client.notifications.archive({ notificationId })
    // Don't clear cache here - let the UI component handle cache updates
    // This allows for more precise cache management
  }

  /**
   * Unarchive notification
   */
  async unarchive(notificationId: string): Promise<void> {
    this.startLocalOperation()
    await this.client.notifications.unarchive({ notificationId })
    // Don't clear cache here - let the UI component handle cache updates
  }

  /**
   * Mark all as read
   * Updates cache optimistically - marks all notifications as read without clearing cache
   */
  async readAll(filter: NotificationFilter = {}): Promise<void> {
    this.startLocalOperation()

    // Optimistically update cache before API call
    this.cache.forEach((entry, key) => {
      // spread 丢失 Notification 类原型方法(消费方只读数据字段),cast 维持既有行为
      const updatedNotifications = entry.notifications.map(notification => ({
        ...notification,
        isRead: true
      }) as Notification)
      this.cache.set(key, {
        ...entry,
        notifications: updatedNotifications
      })
    })

    console.log('[NovuClientWrapper] Optimistically marked all notifications as read in cache')

    // Call API
    await this.client.notifications.readAll(filter)
  }

  /**
   * Archive all
   * Updates cache optimistically - moves all notifications from Inbox to Archive cache
   */
  async archiveAll(filter: NotificationFilter = {}): Promise<void> {
    this.startLocalOperation()
    // Get Inbox cache key
    const inboxCacheKey = this.getCacheKey({ ...filter, archived: false })
    const archiveCacheKey = this.getCacheKey({ ...filter, archived: true })

    const inboxCache = this.cache.get(inboxCacheKey)

    if (inboxCache && inboxCache.notifications.length > 0) {
      // Mark all inbox notifications as archived and read
      const archivedNotifications = inboxCache.notifications.map(notification => ({
        ...notification,
        isArchived: true,
        isRead: true
      }) as Notification)

      // Clear inbox cache
      this.cache.set(inboxCacheKey, {
        notifications: [],
        hasMore: false,
        filter: { ...filter, archived: false },
        timestamp: Date.now()
      })

      // Add to archive cache
      const archiveCache = this.cache.get(archiveCacheKey)
      if (archiveCache) {
        // Merge with existing archive cache (prepend new ones)
        this.cache.set(archiveCacheKey, {
          ...archiveCache,
          notifications: [...archivedNotifications, ...archiveCache.notifications],
          timestamp: Date.now()
        })
      } else {
        // Create new archive cache
        this.cache.set(archiveCacheKey, {
          notifications: archivedNotifications,
          hasMore: false,
          filter: { ...filter, archived: true },
          timestamp: Date.now()
        })
      }

      console.log(`[NovuClientWrapper] Moved ${archivedNotifications.length} notifications from Inbox to Archive cache`)
    }

    // Call API
    await this.client.notifications.archiveAll(filter)

    // Sync cache from Feed API to ensure consistency
    // This will fetch all notifications and distribute them to correct caches
    await this.syncCacheFromFeedAPI()
  }

  /**
   * Archive all read notifications
   * Uses the read-archive endpoint
   * Updates cache optimistically - moves only read notifications from Inbox to Archive cache
   */
  async archiveRead(filter: NotificationFilter = {}): Promise<void> {
    this.startLocalOperation()
    // Get Inbox cache key
    const inboxCacheKey = this.getCacheKey({ ...filter, archived: false })
    const archiveCacheKey = this.getCacheKey({ ...filter, archived: true })

    const inboxCache = this.cache.get(inboxCacheKey)

    if (inboxCache && inboxCache.notifications.length > 0) {
      // Separate read and unread notifications
      const readNotifications = inboxCache.notifications.filter(n => n.isRead)
      const unreadNotifications = inboxCache.notifications.filter(n => !n.isRead)

      if (readNotifications.length > 0) {
        // Mark read notifications as archived
        const archivedNotifications = readNotifications.map(notification => ({
          ...notification,
          isArchived: true
        }) as Notification)

        // Update inbox cache to only contain unread notifications
        this.cache.set(inboxCacheKey, {
          ...inboxCache,
          notifications: unreadNotifications,
          timestamp: Date.now()
        })

        // Add to archive cache
        const archiveCache = this.cache.get(archiveCacheKey)
        if (archiveCache) {
          // Merge with existing archive cache (prepend new ones)
          this.cache.set(archiveCacheKey, {
            ...archiveCache,
            notifications: [...archivedNotifications, ...archiveCache.notifications],
            timestamp: Date.now()
          })
        } else {
          // Create new archive cache
          this.cache.set(archiveCacheKey, {
            notifications: archivedNotifications,
            hasMore: false,
            filter: { ...filter, archived: true },
            timestamp: Date.now()
          })
        }

        console.log(`[NovuClientWrapper] Moved ${archivedNotifications.length} read notifications from Inbox to Archive cache`)
      }
    }

    // Call API
    await this.client.notifications.archiveAllRead(filter)

    // Sync cache from Feed API to ensure consistency
    await this.syncCacheFromFeedAPI()
  }

  /**
   * Update a notification in cache (optimistic update)
   */
  private updateNotificationInCache(
    notificationId: string,
    updates: Partial<Notification>
  ): void {
    this.cache.forEach((entry, key) => {
      const updated = entry.notifications.map((n) =>
        n.id === notificationId ? ({ ...n, ...updates } as Notification) : n
      )
      this.cache.set(key, { ...entry, notifications: updated })
    })
  }

  /**
   * Clear cache for specific filter
   */
  clearCache(filter: NotificationFilter): void {
    const cacheKey = this.getCacheKey(filter)
    const existed = this.cache.has(cacheKey)
    this.cache.delete(cacheKey)
    // 强制刷新不能搭上清缓存之前发出的同条件请求
    for (const key of Array.from(this.inflightLists.keys())) {
      if (key.startsWith(`${cacheKey}|`)) this.inflightLists.delete(key)
    }
    console.log('[NovuClientWrapper] Cache cleared for:', cacheKey, 'existed:', existed)
  }

  /**
   * Get and clear pending notifications (called when loading new notifications)
   */
  getPendingNotifications(): Notification[] {
    const pending = [...this.pendingNotifications]
    console.log('[NovuClientWrapper] Getting pending notifications, count:', pending.length)
    this.pendingNotifications = []
    return pending
  }

  /**
   * Clear pending notifications
   */
  clearPendingNotifications(): void {
    console.log('[NovuClientWrapper] Clearing pending notifications, count:', this.pendingNotifications.length)
    this.pendingNotifications = []
  }

  /**
   * Clear all cache
   */
  clearAllCache(): void {
    this.cache.clear()
    // 登出 / token 过期：作废补齐状态，进行中的请求回来后按 epoch 丢弃（不写进新会话、不发事件）
    this.epoch += 1
    this.resolvedMessages.clear()
    this.inflightMessages.clear()
    this.inflightLists.clear()
    this.pendingNotifications = []
    console.log('[NovuClientWrapper] All cache cleared')
  }

  /**
   * Update cache with new notifications list (for manual cache updates)
   */
  updateCacheManually(filter: NotificationFilter, notifications: Notification[], hasMore: boolean): void {
    const cacheKey = this.getCacheKey(filter)
    this.cache.set(cacheKey, {
      // UI 回写的可能是补齐前的引用（补齐事件与回写交错时），存之前套一遍已知结果
      notifications: notifications.map((n) => this.applyKnownEnrichment(n)),
      hasMore,
      filter,
      timestamp: Date.now(),
    })
    console.log('[NovuClientWrapper] Cache manually updated for:', cacheKey, 'count:', notifications.length)
  }

  /**
   * Sync cache from Novu Inbox API after archive operations
   * Fetches inbox and archived notifications via SDK list and refreshes both caches.
   * 注意：之前用 legacy Feed API + 客户端 split，但 Feed API 不支持 archived 过滤，
   * 导致 archive cache 永远拿不到归档数据。改用 SDK 的 Inbox list API（与 mobile 一致）。
   */
  async syncCacheFromFeedAPI(): Promise<void> {
    try {
      console.log('[NovuClientWrapper] 🔄 Syncing cache from Inbox API')

      // Get both cache entries to determine total count
      const inboxFilter: NotificationFilter = { tags: [], archived: false, snoozed: false }
      const archiveFilter: NotificationFilter = { tags: [], archived: true, snoozed: false }
      const inboxCacheKey = this.getCacheKey(inboxFilter)
      const archiveCacheKey = this.getCacheKey(archiveFilter)
      const inboxCache = this.cache.get(inboxCacheKey)
      const archiveCache = this.cache.get(archiveCacheKey)

      const totalCount = (inboxCache?.notifications?.length || 0) + (archiveCache?.notifications?.length || 0)
      // Fetch cache total + 10 buffer (to catch new notifications), max 100
      const fetchLimit = Math.min(totalCount + 10, 100)

      console.log('[NovuClientWrapper] 📊 Current cache state:', {
        inbox: inboxCache?.notifications?.length || 0,
        archive: archiveCache?.notifications?.length || 0,
        fetchLimit
      })

      // Inbox API 一次请求只返回一个 archived 状态的数据，所以分两次并发取
      const [inboxResp, archiveResp] = await Promise.all([
        this.client.notifications.list({ tags: [], archived: false, snoozed: false, limit: fetchLimit }),
        this.client.notifications.list({ tags: [], archived: true, snoozed: false, limit: fetchLimit }),
      ])

      // 换进缓存前先套已知补齐结果：否则每次远端变更同步都把已补好的条目换回残缺版本，
      // 下次打开收件箱又降级渲染。没补过的不在这里发请求（收件箱可能关着、条目可能上百），
      // 等 list() 命中缓存时后台补。
      const inboxNotifications = (inboxResp?.data?.notifications || []).map((n) => this.applyKnownEnrichment(n))
      const archiveNotifications = (archiveResp?.data?.notifications || []).map((n) => this.applyKnownEnrichment(n))
      const inboxHasMore = inboxResp?.data?.hasMore || false
      const archiveHasMore = archiveResp?.data?.hasMore || false

      console.log('[NovuClientWrapper] 📥 Fetched from Inbox API:', {
        inbox: inboxNotifications.length,
        archive: archiveNotifications.length,
        inboxHasMore,
        archiveHasMore,
      })

      // Update Inbox cache - use API's hasMore value
      this.cache.set(inboxCacheKey, {
        notifications: inboxNotifications.slice(0, 100),
        hasMore: inboxHasMore || inboxNotifications.length > 100,
        filter: inboxFilter,
        timestamp: Date.now()
      })

      // Update Archive cache - use API's hasMore value
      this.cache.set(archiveCacheKey, {
        notifications: archiveNotifications.slice(0, 100),
        hasMore: archiveHasMore || archiveNotifications.length > 100,
        filter: archiveFilter,
        timestamp: Date.now()
      })

      console.log('[NovuClientWrapper] ✅ Cache synced successfully')

      // Emit cache updated event
      this.emit('cache_updated', { tab: 'both' })

    } catch (error) {
      console.error('[NovuClientWrapper] ❌ Failed to sync cache from Inbox API:', error)
      throw error
    }
  }

  /**
   * Get cache data for a specific filter
   */
  getCacheData(filter: NotificationFilter): NotificationCacheEntry | null {
    const cacheKey = this.getCacheKey(filter)
    return this.cache.get(cacheKey) || null
  }

  /**
   * Get cache stats (for debugging)
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      entries: Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        count: entry.notifications.length,
        age: Date.now() - entry.timestamp,
      })),
    }
  }

  /**
   * Get full cache data (for debugging in browser console)
   */
  getFullCacheData() {
    return {
      size: this.cache.size,
      caches: Array.from(this.cache.entries()).map(([key, entry]) => ({
        cacheKey: key,
        notificationCount: entry.notifications.length,
        hasMore: entry.hasMore,
        timestamp: entry.timestamp,
        age: Date.now() - entry.timestamp,
        notifications: entry.notifications,
      })),
      pendingNotifications: this.pendingNotifications,
      pendingCount: this.pendingNotifications.length,
    }
  }

  /**
   * Event emitter - on
   */
  on(event: NotificationEventType, handler: NotificationEventHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler)
  }

  /**
   * Event emitter - off
   */
  off(event: NotificationEventType, handler: NotificationEventHandler): void {
    this.listeners.get(event)?.delete(handler)
  }

  /**
   * Event emitter - emit
   */
  private emit(event: NotificationEventType, data?: any): void {
    this.listeners.get(event)?.forEach((handler) => handler(data))
  }
}
